import { useEffect, useMemo, useRef, useState } from "react";
import { dataCurta, dataPorExtenso, diaDaSemana, diasEntre, mesEAnoPorExtenso, somarDias } from "@noesis/shared";
import { Icone } from "./Icone.js";
import { IconeAvancar, IconeCalendario, IconeFechar, IconeVoltar } from "./icones.js";

/**
 * O seletor de dia do heatmap — o caminho alternativo que a RN-56 exige.
 *
 * **Por que ele existe, e por que ele não pode ser opcional.** A RN-56 isenta as
 * 365 células do heatmap do alvo mínimo de 44px, e a isenção é **condicional**:
 * ela vale enquanto o mesmo dia for alcançável por outro caminho. Este é o
 * caminho. `heatmap-isencao.portao.test.ts` falha se ele sair sem a isenção sair
 * junto.
 *
 * **Era um `<input type="date">`, e o nativo não servia.** Não por gosto: o
 * calendário do navegador é desenhado pelo sistema operacional, não pela folha
 * do app — ele ignora tema, tipografia, superfície e a cor de ritual, e o único
 * controle que se tem sobre ele é `color-scheme`. Num app que decide cada
 * superfície por luminância, ele abria uma janela branca de sistema no meio do
 * pergaminho. E o campo fechado ainda mostrava a data em `09/13/2026`, no
 * formato do locale do navegador, enquanto todo o resto da tela lê
 * `13/09/2026` pelo formatador único do `@noesis/shared`. ADR 029.
 *
 * **O que ele NÃO faz, e de propósito:** ele não digita. Um campo de texto de
 * data cobra máscara, validação, mensagem de erro e um teclado numérico no
 * celular — quatro coisas para uma consulta que é sempre "aquele dia ali".
 * Quem quer um dia específico o encontra na grade; quem quer voltar para hoje
 * tem um alvo próprio.
 *
 * É um `<dialog>` nativo pela mesma razão do seletor de trilha e da folha
 * "Mais": o navegador já dá o laço de foco, o Escape e o fundo inerte, e
 * reescrever isso à mão é o tipo de comportamento acessível difícil de acertar
 * que a identidade visual manda não reimplementar.
 *
 * O resultado da escolha **não é anunciado aqui**. Quem anuncia é o
 * `aria-live` do detalhe do dia, no heatmap: o dado que interessa é quantas
 * respostas aquele dia teve, e anunciar a data duas vezes — uma pelo controle,
 * outra pelo detalhe — faria o leitor de tela ler o meio da frase antes do
 * começo dela.
 */

/*
 * Três letras, e não uma. "D S T Q Q S S" é ambíguo em dois pares — S de segunda
 * e de sábado, Q de quarta e de quinta —, e numa coluna de 44px as três letras
 * cabem com folga. É o mesmo vocabulário do eixo do heatmap logo acima, e ler
 * dois alfabetos diferentes para a mesma semana na mesma tela não ajuda ninguém.
 */
const DIAS_DA_SEMANA_CURTOS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const DIAS_DA_SEMANA_LONGOS = ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"];

/** O primeiro dia do mês de uma data, sem passar por `Date` (invariante 16). */
function primeiroDoMes(dia: string): string {
  return `${dia.slice(0, 7)}-01`;
}

function mesDe(dia: string): string {
  return dia.slice(0, 7);
}

/**
 * O mesmo dia, `n` meses adiante ou atrás, **sem cair no mês errado**.
 *
 * A conta ingênua — somar ou subtrair o número de dias do mês vizinho — falha no
 * fim do mês, e falha em silêncio: partindo de 31 de março, subtrair os 28 dias
 * de fevereiro dá 3 de **março**, e PageUp não troca de mês nenhuma. Aqui o mês
 * é aritmética de mês, e o dia fica preso ao último que o mês de destino tem:
 * 31 de março para trás é 28 de fevereiro, não 3 de março.
 */
function somarMeses(dia: string, n: number): string {
  const ano = Number(dia.slice(0, 4));
  const mes = Number(dia.slice(5, 7)) - 1 + n;
  const data = Number(dia.slice(8));
  const anoAlvo = ano + Math.floor(mes / 12);
  const mesAlvo = ((mes % 12) + 12) % 12;
  const chave = `${String(anoAlvo).padStart(4, "0")}-${String(mesAlvo + 1).padStart(2, "0")}`;
  const ultimo = diasNoMes(`${chave}-01`);
  return `${chave}-${String(Math.min(data, ultimo)).padStart(2, "0")}`;
}

/**
 * As semanas de um mês, alinhadas por domingo — a mesma grade do heatmap.
 *
 * Os dias de fora do mês entram como `null` em vez de virarem dias do mês
 * vizinho: dia clicável fora do mês que o cabeçalho anuncia é o jeito mais
 * rápido de escolher outro mês sem perceber.
 */
function semanasDoMes(mes: string): (string | null)[][] {
  const primeiro = `${mes}-01`;
  const semanas: (string | null)[][] = [];
  let atual: (string | null)[] = Array.from({ length: diaDaSemana(primeiro) }, () => null);

  for (let dia = primeiro; mesDe(dia) === mes; dia = somarDias(dia, 1)) {
    atual.push(dia);
    if (atual.length === 7) {
      semanas.push(atual);
      atual = [];
    }
  }
  if (atual.length > 0) {
    while (atual.length < 7) atual.push(null);
    semanas.push(atual);
  }
  return semanas;
}

export function SeletorDeDia({
  valor,
  de,
  ate,
  aoEscolher,
}: {
  /** O dia escolhido, em ISO. */
  valor: string;
  /** O começo da faixa alcançável — o começo do heatmap. */
  de: string;
  /** O fim da faixa alcançável — hoje. */
  ate: string;
  aoEscolher: (dia: string) => void;
}) {
  const [aberto, setAberto] = useState(false);
  /**
   * Quantas vezes o diálogo já abriu. Ele é a `key` do calendário, e isso
   * resolve duas coisas de uma vez (ADR 030).
   *
   * O calendário guarda mês visível e dia com foco, e reabrir precisa voltar ao
   * mês do dia escolhido em vez de continuar onde a última visita parou — era o
   * que o `{aberto && …}` fazia, desmontando o conteúdo ao fechar.
   *
   * **Só que desmontar ao fechar esvazia a folha no meio da saída.** Com a
   * transição de saída, o `<dialog>` fica na tela mais 120ms enquanto desce, e
   * nesses 120ms o conteúdo já não existia: a folha deslizava vazia. Trocar a
   * `key` a cada abertura reinicia o estado do mesmo jeito e mantém o conteúdo
   * montado até o fim da saída.
   *
   * Diálogo fechado não custa nada: a folha do agente de usuário lhe dá
   * `display: none`, e os 42 alvos da grade não são renderizados nem focáveis.
   */
  const [aberturas, setAberturas] = useState(0);
  const dialogo = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const d = dialogo.current;
    if (!d) return;
    if (aberto && !d.open) d.showModal();
    if (!aberto && d.open) d.close();
  }, [aberto]);

  function abrir() {
    setAberturas((n) => n + 1);
    setAberto(true);
  }

  return (
    <div className="seletor-dia">
      {/* O nome do controle, à esquerda dele. Não é `<label>` e não aponta para
          o botão por `id`: o nome acessível do botão é o `aria-label` abaixo,
          que diz a mesma coisa **mais** a data em que ele está. Dois nomes para
          o mesmo alvo é o que faz o leitor de tela ler o rótulo duas vezes. */}
      <span className="legenda">Ver um dia</span>
      <button
        type="button"
        className="seletor-dia__gatilho"
        aria-haspopup="dialog"
        aria-expanded={aberto}
        // O rótulo visível é a data; o acessível diz também o que o controle
        // faz, que o desenho do calendário sozinho não diz.
        aria-label={`Ver um dia: ${dataPorExtenso(valor)}. Escolher outro dia`}
        onClick={abrir}
      >
        <Icone glifo={IconeCalendario} papel="acao" />
        <span className="dado">{dataCurta(valor)}</span>
      </button>

      <dialog
        ref={dialogo}
        className="folha-mais folha-dia"
        aria-label="Escolher um dia"
        onClose={() => setAberto(false)}
        onClick={(e) => {
          if (e.target === dialogo.current) setAberto(false);
        }}
      >
        {/* A `key` reinicia o calendário a cada abertura sem desmontá-lo ao
            fechar — ver o comentário de `aberturas` lá em cima. */}
        <Calendario
          key={aberturas}
          valor={valor}
          de={de}
          ate={ate}
          aoEscolher={(dia) => {
            aoEscolher(dia);
            setAberto(false);
          }}
          aoFechar={() => setAberto(false)}
        />
      </dialog>
    </div>
  );
}

function Calendario({
  valor,
  de,
  ate,
  aoEscolher,
  aoFechar,
}: {
  valor: string;
  de: string;
  ate: string;
  aoEscolher: (dia: string) => void;
  aoFechar: () => void;
}) {
  /**
   * O dia com foco, que não é o dia escolhido.
   *
   * A grade é um **tabindex rotativo**: um só dos 28 a 31 botões é alcançável
   * pela tabulação, e as setas movem o foco dentro dela. Sem isso, sair do
   * calendário pelo teclado custa trinta e uma tabulações — que é a diferença
   * entre um controle utilizável e um controle que existe no relatório.
   */
  const [focado, setFocado] = useState(valor);
  const mes = mesDe(focado);
  const botoes = useRef(new Map<string, HTMLButtonElement>());
  /** O foco só é movido por tecla; abrir o diálogo não rouba o foco da grade. */
  const moverFoco = useRef(false);

  useEffect(() => {
    if (!moverFoco.current) return;
    moverFoco.current = false;
    botoes.current.get(focado)?.focus();
  }, [focado]);

  const semanas = useMemo(() => semanasDoMes(mes), [mes]);

  const dentroDaFaixa = (dia: string) => diasEntre(de, dia) >= 0 && diasEntre(dia, ate) >= 0;

  /** Anda `n` dias e para nas pontas da faixa — nunca sai dela. */
  function andar(n: number) {
    const alvo = somarDias(focado, n);
    if (!dentroDaFaixa(alvo)) return;
    moverFoco.current = true;
    setFocado(alvo);
  }

  /**
   * Vai para um dia, **grudando na ponta** quando o alvo cai fora da faixa.
   *
   * Grudar e não ignorar: PageUp em janeiro, com a faixa começando em setembro
   * do ano anterior, precisa parar em setembro — não ficar parado sem dizer
   * nada. Tecla que não faz nada é tecla que parece quebrada.
   */
  function irPara(dia: string) {
    const alvo = diasEntre(de, dia) < 0 ? de : diasEntre(dia, ate) < 0 ? ate : dia;
    if (alvo === focado) return;
    moverFoco.current = true;
    setFocado(alvo);
  }

  function aoTeclar(e: React.KeyboardEvent) {
    const passos: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
    };
    if (e.key in passos) {
      e.preventDefault();
      andar(passos[e.key]!);
      return;
    }
    // Mês é aritmética de mês, nunca de dias — ver `somarMeses`.
    if (e.key === "PageUp" || e.key === "PageDown") {
      e.preventDefault();
      irPara(somarMeses(focado, e.key === "PageUp" ? -1 : 1));
      return;
    }
    if (e.key === "Home") {
      e.preventDefault();
      irPara(somarDias(focado, -diaDaSemana(focado)));
      return;
    }
    if (e.key === "End") {
      e.preventDefault();
      irPara(somarDias(focado, 6 - diaDaSemana(focado)));
    }
  }

  const mesAnterior = somarDias(primeiroDoMes(focado), -1);
  const mesSeguinte = somarDias(primeiroDoMes(focado), diasNoMes(focado));

  return (
    <div className="folha-mais__corpo">
      <div className="folha-mais__topo">
        <span className="etiqueta">Escolher um dia</span>
        <button type="button" className="lateral__alternar" aria-label="Fechar" onClick={aoFechar}>
          <Icone glifo={IconeFechar} papel="acao" />
        </button>
      </div>

      <div className="calendario__mes">
        <button
          type="button"
          className="calendario__passo"
          aria-label={`Mês anterior: ${mesEAnoPorExtenso(mesAnterior)}`}
            title={`Mês anterior: ${mesEAnoPorExtenso(mesAnterior)}`}
          // Desligada quando nem o ÚLTIMO dia do mês anterior alcança a faixa:
          // se ele já ficou atrás de `de`, nenhum dia daquele mês entra.
          disabled={diasEntre(de, mesAnterior) < 0}
          onClick={() => irPara(maiorDeDois(mesAnterior, de))}
        >
          <Icone glifo={IconeVoltar} papel="acao" />
        </button>
        {/* `aria-live` aqui e não no botão: trocar de mês pelo teclado precisa
            anunciar em que mês o foco caiu, ou a navegação vira adivinhação. */}
        <span className="calendario__titulo" aria-live="polite">
          {mesEAnoPorExtenso(focado)}
        </span>
        <button
          type="button"
          className="calendario__passo"
          aria-label={`Próximo mês: ${mesEAnoPorExtenso(mesSeguinte)}`}
            title={`Próximo mês: ${mesEAnoPorExtenso(mesSeguinte)}`}
          disabled={diasEntre(mesSeguinte, ate) < 0}
          onClick={() => irPara(menorDeDois(mesSeguinte, ate))}
        >
          <Icone glifo={IconeAvancar} papel="acao" />
        </button>
      </div>

      {/*
        `<table>` de verdade, como o heatmap: o leitor de tela percorre a grade
        pelo cabeçalho de dia da semana, que é como um calendário se lê. Uma
        grade de `<div>` com `role="grid"` daria o mesmo desenho e obrigaria a
        reescrever à mão a navegação que a tabela já tem.
      */}
      <table className="calendario__grade" onKeyDown={aoTeclar}>
        <caption className="so-leitor">{mesEAnoPorExtenso(focado)}</caption>
        <thead>
          <tr>
            {DIAS_DA_SEMANA_CURTOS.map((letra, i) => (
              <th key={DIAS_DA_SEMANA_LONGOS[i]} scope="col" className="calendario__semana">
                <span aria-hidden="true">{letra}</span>
                <span className="so-leitor">{DIAS_DA_SEMANA_LONGOS[i]}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {semanas.map((semana, i) => (
            <tr key={`${mes}-s${i}`}>
              {semana.map((dia, j) => (
                <td key={dia ?? `vazio-${mes}-${i}-${j}`} className="calendario__caixa">
                  {dia && (
                    <button
                      type="button"
                      ref={(el) => {
                        if (el) botoes.current.set(dia, el);
                        else botoes.current.delete(dia);
                      }}
                      className={dia === valor ? "calendario__dia calendario__dia--escolhido" : "calendario__dia"}
                      // Um só alcançável pela tabulação: as setas fazem o resto.
                      tabIndex={dia === focado ? 0 : -1}
                      disabled={!dentroDaFaixa(dia)}
                      aria-current={dia === valor ? "date" : undefined}
                      aria-label={dataPorExtenso(dia)}
                      onFocus={() => setFocado(dia)}
                      onClick={() => aoEscolher(dia)}
                    >
                      <span aria-hidden="true">{Number(dia.slice(8))}</span>
                    </button>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {/* O atalho que a grade não dá de graça: voltar para hoje custaria uma
          navegação de mês por mês desde onde a pessoa estivesse. */}
      <button className="botao botao--secundario" type="button" onClick={() => aoEscolher(ate)}>
        Hoje
      </button>
    </div>
  );
}

/** Quantos dias tem o mês de uma data. */
function diasNoMes(dia: string): number {
  const primeiro = primeiroDoMes(dia);
  let n = 0;
  for (let d = primeiro; mesDe(d) === mesDe(primeiro); d = somarDias(d, 1)) n++;
  return n;
}

const maiorDeDois = (a: string, b: string) => (diasEntre(a, b) > 0 ? b : a);
const menorDeDois = (a: string, b: string) => (diasEntre(a, b) > 0 ? a : b);
